import React, { useState, useEffect } from 'react';
import { toViewerZone, viewerTz } from '../utils/timeZone';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { formatDate as formatDateUtil } from '../utils/dateFormat';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Calendar,
  Clock,
  MapPin,
  User,
  Video,
  Phone,
  Loader2,
  ArrowRight,
  RefreshCw,
  Plus
} from 'lucide-react';
import { format, parseISO, isToday, isTomorrow, isThisWeek, addDays } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const APPOINTMENT_TYPES = {
  'initial_assessment': { label: 'Initial Assessment', color: 'bg-blue-500' },
  'follow_up': { label: 'Follow-up Meeting', color: 'bg-green-500' },
  'case_review': { label: 'Case Review', color: 'bg-purple-500' },
  'intake': { label: 'Intake Appointment', color: 'bg-orange-500' },
  'phone_call': { label: 'Phone Call', color: 'bg-teal-500' },
  'training': { label: 'Training Session', color: 'bg-indigo-500' },
  'other': { label: 'Other', color: 'bg-gray-500' }
};

const LOCATION_ICONS = {
  'in_person': MapPin,
  'phone': Phone,
  'video': Video
};

const AppointmentsWidget = ({ limit = 5, showHeader = true, compact = false }) => {
  const { user } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [userTimezone, setUserTimezone] = useState('America/Chicago');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch system settings for timezone
      const settingsRes = await axios.get(`${API}/system/settings`);
      if (settingsRes.data?.timezone) {
        setUserTimezone(settingsRes.data.timezone);
      }

      // Fetch upcoming appointments (next 7 days)
      const startDate = format(new Date(), 'yyyy-MM-dd');
      const endDate = format(addDays(new Date(), 7), 'yyyy-MM-dd');
      
      const appointmentsRes = await axios.get(`${API}/appointments`, {
        params: { start_date: startDate, end_date: endDate }
      });
      
      // Sort by date and time, filter to only scheduled/upcoming
      const upcomingAppointments = (appointmentsRes.data.appointments || []).map(a => toViewerZone(a, viewerTz(user)))
        .filter(apt => (apt.status || 'scheduled').toLowerCase() === 'scheduled')
        .sort((a, b) => {
          const dateA = new Date(`${a.date}T${a.start_time}`);
          const dateB = new Date(`${b.date}T${b.start_time}`);
          return dateA - dateB;
        })
        .slice(0, limit);
      
      setAppointments(upcomingAppointments);
    } catch (error) {
      console.error('Failed to fetch appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  const getDateLabel = (dateStr) => {
    const date = parseISO(dateStr);
    if (isToday(date)) return 'Today';
    if (isTomorrow(date)) return 'Tomorrow';
    if (isThisWeek(date)) return format(date, 'EEEE');
    return formatDateUtil(dateStr);
  };

  const formatTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const getTypeInfo = (type) => {
    return APPOINTMENT_TYPES[type] || APPOINTMENT_TYPES['other'];
  };

  if (loading) {
    return (
      <Card className="card-shadow">
        {showHeader && (
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              Upcoming Appointments
            </CardTitle>
          </CardHeader>
        )}
        <CardContent className="flex items-center justify-center h-48">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-shadow" data-testid="appointments-widget">
      {showHeader && (
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              <CardTitle className="text-lg">Upcoming Appointments</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={handleRefresh}
                disabled={refreshing}
                className="h-8 w-8"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
              <Button variant="outline" size="sm" asChild>
                <Link to="/appointments">
                  <Plus className="w-4 h-4 mr-1" />
                  New
                </Link>
              </Button>
            </div>
          </div>
          <CardDescription>
            Your schedule for the next 7 days
          </CardDescription>
        </CardHeader>
      )}
      <CardContent className={showHeader ? '' : 'pt-6'}>
        {appointments.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="text-sm">No upcoming appointments</p>
            <Button variant="outline" size="sm" className="mt-4" asChild>
              <Link to="/appointments">
                Schedule Appointment
              </Link>
            </Button>
          </div>
        ) : (
          <ScrollArea className={compact ? "h-[200px]" : "h-[280px]"}>
            <div className="space-y-3">
              {appointments.map((appointment) => {
                const typeInfo = getTypeInfo(appointment.appointment_type);
                const LocationIcon = LOCATION_ICONS[appointment.location_type] || MapPin;
                
                return (
                  <div 
                    key={appointment.id} 
                    className="flex items-start gap-3 p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                    data-testid={`appointment-${appointment.id}`}
                  >
                    {/* Date indicator */}
                    <div className="flex flex-col items-center min-w-[50px] text-center">
                      <span className="text-xs font-medium text-muted-foreground">
                        {getDateLabel(appointment.date)}
                      </span>
                      <span className="text-lg font-bold text-foreground">
                        {format(parseISO(appointment.date), 'd')}
                      </span>
                    </div>
                    
                    {/* Appointment details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <div className={`w-2 h-2 rounded-full ${typeInfo.color}`} />
                        <span className="font-medium text-sm truncate">
                          {appointment.title || typeInfo.label}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatTime(appointment.start_time)}
                          {appointment.end_time && ` - ${formatTime(appointment.end_time)}`}{appointment.tz_abbr ? ` ${appointment.tz_abbr}` : ''}
                        </span>
                        
                        {appointment.location_type && (
                          <span className="flex items-center gap-1">
                            <LocationIcon className="w-3 h-3" />
                            {appointment.location_type === 'in_person' ? 'In Person' : 
                             appointment.location_type === 'phone' ? 'Phone' : 'Video'}
                          </span>
                        )}
                      </div>
                      
                      {appointment.participant_name && (
                        <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                          <User className="w-3 h-3" />
                          <span className="truncate">{appointment.participant_name}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Type badge - compact */}
                    {!compact && (
                      <Badge variant="outline" className="text-xs shrink-0">
                        {typeInfo.label.split(' ')[0]}
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
        
        {appointments.length > 0 && (
          <div className="mt-4 pt-3 border-t">
            <Button variant="ghost" className="w-full" asChild>
              <Link to="/appointments">
                View All Appointments
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AppointmentsWidget;
